/**
 * EditButtonRenderer — renders edit-mode buttons on graph nodes.
 *
 * Each node belonging to the selected recipe gets a set of SVG buttons:
 *   - (+) add parent/child link
 *   - (−) delete parent/child link — only if node has parents/children
 *   - (×) delete node
 *
 * All buttons match the style of the existing fold/unfold caret:
 *   black background (#0d1117), green accent border (alpha-40), green symbols.
 *   All are placed at the same radial distance from the node center as the caret
 *   (NODE_RADIUS + BTN_RADIUS = 58px).
 *
 * Desktop: buttons appear on hover (not on the selected node to avoid clutter).
 * Mobile:  buttons appear only on the selected node (no hover on touch devices).
 *
 * See docs/design/editing-capabilities.md §4 for the design specification.
 */

import type { Selection, BaseType } from 'd3-selection';
import { DisplayNode } from '../recipe_renderer/types.js';
import { NODE_RADIUS } from '../recipe_renderer/dimensions.js';

/** Button radius — matching the fold/unfold caret (r=13) */
const BTN_RADIUS = 13;

/**
 * Distance from node center for axis-aligned buttons (top/bottom).
 * Matches the caret's radial distance: sqrt(41²+41²) = NODE_RADIUS + BTN_RADIUS = 58.
 */
const AXIS_DIST = NODE_RADIUS + BTN_RADIUS; // 58

/**
 * Offset for diagonal buttons (× at top-right).
 * Matches the caret's translate: NODE_RADIUS - 4 = 41.
 */
const DIAG_OFF = NODE_RADIUS - 4; // 41

/** Font size for button icons — slightly bigger for readability */
const BTN_ICON_SIZE = '17px';

/**
 * Shared button style matching the caret button exactly:
 *   fill:   #0d1117  (near-black)
 *   stroke: var(--accent-alpha-40)
 *   sw:     1.5
 *   symbol: var(--accent)
 */
const BTN_FILL = '#0d1117';
const BTN_STROKE = 'var(--accent-alpha-40)';
const BTN_STROKE_WIDTH = 1.5;
const BTN_SYMBOL_FILL = 'var(--accent)';

// ── Layout offsets (polar coordinates) ───────────────────────────────────
//   Radius is fixed at AXIS_DIST = NODE_RADIUS + BTN_RADIUS = 58.
//   The caret (>) is at 45°  (DIAG_OFF, DIAG_OFF).
//   The × delete  is at 45°  (DIAG_OFF, -DIAG_OFF).
//   The ＋/－ buttons are at ±15° from vertical, so they don't overlap × or >.
//
//   Top:    −parent at angle -15°  →  (−R*sinθ, -R*cosθ) = (−15, −56)
//           ＋parent at angle +15° →  (+15, −56)   (when − exists; else cx=0)
//           × at angle +45°        →  (+41, −41)
//
//   Bottom: −child at angle -15°   →  (−15, +56)
//           ＋child at angle +15°  →  (+15, +56)   (when − exists; else cx=0)
//           caret at angle -45°    →  (+41, +41)
//
const POLAR_ANGLE_DEG = 15;
const POLAR_SIN = Math.round(AXIS_DIST * Math.sin(POLAR_ANGLE_DEG * Math.PI / 180)); // 15
const POLAR_COS = Math.round(AXIS_DIST * Math.cos(POLAR_ANGLE_DEG * Math.PI / 180)); // 56

/**
 * EditButtonRenderer — creates and manages edit button SVG groups on each node.
 *
 * Usage:
 *   const renderer = new EditButtonRenderer({ ... });
 *   renderer.renderAll(nodePool);         // In edit mode renderAll()
 *   renderer.updateVisibility(sel, hover); // On selection/hover change
 *   renderer.removeAll();                  // On edit mode exit
 */
export class EditButtonRenderer {
  private nodesGroup: Selection<SVGGElement, DisplayNode, BaseType, unknown>;
  // Buttons only render for the *focused* recipe's nodes — in "global"
  // edition mode (nothing focused) no per-node canvas buttons show at all,
  // even if a recipe/node happens to be selected while browsing unfocused.
  private focusedRecipeId: () => string | null;
  private nodePool: () => Map<string, DisplayNode>;
  private isNodeHidden: (nodeId: string) => boolean;
  private isNodeSelected: (nodeId: string) => boolean;
  private isNodeHovered: (nodeId: string) => boolean;
  private isNodeExpanded: (nodeId: string) => boolean;
  private isMobile: boolean;

  /** Callbacks wired from GraphVisualizer */
  private onAddParentLink: (node: DisplayNode) => void;
  private onAddChildLink: (node: DisplayNode) => void;
  private onDeleteParentLink: (node: DisplayNode) => void;
  private onDeleteChildLink: (node: DisplayNode) => void;
  private onDeleteNode: (node: DisplayNode) => void;

  /** Active touch for disambiguation (tap vs drag, see §8.3.1) */
  private touchStartPos: { x: number; y: number } | null = null;
  private touchStartTime: number = 0;
  private touchActiveNode: DisplayNode | null = null;
  private touchAction: (() => void) | null = null;

  /** Node whose delete (×) button is in the "armed" state, awaiting second click. */
  private _pendingDeleteNodeId: string | null = null;

  constructor(params: {
    nodesGroup: Selection<SVGGElement, DisplayNode, BaseType, unknown>;
    focusedRecipeId: () => string | null;
    nodePool: () => Map<string, DisplayNode>;
    isNodeHidden: (nodeId: string) => boolean;
    isNodeSelected: (nodeId: string) => boolean;
    isNodeHovered: (nodeId: string) => boolean;
    isNodeExpanded: (nodeId: string) => boolean;
    isMobile?: boolean;
    onAddParentLink: (node: DisplayNode) => void;
    onAddChildLink: (node: DisplayNode) => void;
    onDeleteParentLink: (node: DisplayNode) => void;
    onDeleteChildLink: (node: DisplayNode) => void;
    onDeleteNode: (node: DisplayNode) => void;
  }) {
    this.nodesGroup = params.nodesGroup;
    this.focusedRecipeId = params.focusedRecipeId;
    this.nodePool = params.nodePool;
    this.isNodeHidden = params.isNodeHidden;
    this.isNodeSelected = params.isNodeSelected;
    this.isNodeHovered = params.isNodeHovered;
    this.isNodeExpanded = params.isNodeExpanded;
    this.isMobile = params.isMobile ?? false;
    this.onAddParentLink = params.onAddParentLink;
    this.onAddChildLink = params.onAddChildLink;
    this.onDeleteParentLink = params.onDeleteParentLink;
    this.onDeleteChildLink = params.onDeleteChildLink;
    this.onDeleteNode = params.onDeleteNode;
  }

  // ===========================================================================
  // DELETE CONFIRMATION (TWO-CLICK)
  // ===========================================================================

  /**
   * Disarm any pending delete (×) button, resetting its visual style.
   * Safe to call even when nothing is armed.
   */
  disarmDelete(): void {
    if (this._pendingDeleteNodeId) {
      const nodeId = this._pendingDeleteNodeId;
      this._pendingDeleteNodeId = null;
      this.styleDeleteButton(nodeId, false);
    }
  }

  /**
   * Toggle the visual style of a node's × button between normal and "armed".
   * Armed state inverts the colors: green fill, black stroke and symbol.
   */
  private styleDeleteButton(nodeId: string, armed: boolean): void {
    this.nodesGroup.select<SVGGElement>(`#viz-node-${CSS.escape(nodeId)}`)
      .select('.edit-button-group')
      .selectAll('.edit-btn')
      .filter(function () {
        return d3.select(this).select('text').text() === '×';
      })
      .each(function () {
        d3.select(this).select('circle')
          .attr('fill', armed ? 'var(--accent)' : BTN_FILL)
          .attr('stroke', armed ? '#0d1117' : BTN_STROKE);
        d3.select(this).select('text')
          .attr('fill', armed ? '#0d1117' : BTN_SYMBOL_FILL);
      });
  }

  // ===========================================================================
  // MAIN ENTRY POINT
  // ===========================================================================

  /**
   * Render or update edit buttons on all nodes belonging to the focused
   * recipe. Removes buttons from nodes that no longer belong to it. With
   * nothing focused ("global" edition mode), removes every button instead
   * — no per-node canvas buttons apply there, only the toolbar's [+].
   * Clears any pending delete arm state (re-render resets all button styles).
   */
  renderAll(nodePool: Map<string, DisplayNode>): void {
    // Armed state doesn't survive a full re-render
    this._pendingDeleteNodeId = null;

    const recipeId = this.focusedRecipeId();
    if (!recipeId) {
      this.removeAll();
      return;
    }

    this.removeButtonsForNonRecipeNodes(recipeId);

    for (const node of nodePool.values()) {
      if (this.isNodeHidden(node.id)) continue;
      if (node.recipeId !== recipeId) continue;

      const nodeEl = this.nodesGroup.select<SVGGElement>(`#viz-node-${CSS.escape(node.id)}`) as unknown as Selection<SVGGElement, DisplayNode, BaseType | null, unknown>;
      if (nodeEl.empty()) continue;

      this.renderButtonsForNode(nodeEl, node);
    }
  }

  /**
   * Remove all edit buttons from all nodes.
   */
  removeAll(): void {
    this.nodesGroup.selectAll<SVGGElement, DisplayNode>('.viz-node .edit-button-group').remove();
  }

  // ===========================================================================
  // BUTTON VISIBILITY
  // ===========================================================================

  /**
   * Update button visibility on hover/selection change.
   *
   * When a node is hovered, only that node's buttons show (not the selected
   * node's) — this avoids clutter.  When nothing is hovered, the selected
   * node's buttons are visible.
   * On mobile (where there's no hover state), buttons only appear on the
   * selected node.
   */
  updateVisibility(selectedNode: DisplayNode | null, hoveredNode: DisplayNode | null): void {
    const showHovered = hoveredNode != null;

    this.nodesGroup.selectAll<SVGGElement, DisplayNode>('.viz-node .edit-button-group')
      .style('opacity', (d: DisplayNode) => {
        const visible = showHovered ? this.isNodeHovered(d.id) : this.isNodeSelected(d.id);
        return visible ? 1 : 0;
      })
      .style('pointer-events', (d: DisplayNode) => {
        const visible = showHovered ? this.isNodeHovered(d.id) : this.isNodeSelected(d.id);
        return visible ? 'auto' : 'none';
      });

    // Also toggle the mobile touch hit area rects — their explicit
    // pointer-events:all overrides the parent's pointer-events:none.
    this.nodesGroup.selectAll<SVGGElement, DisplayNode>('.viz-node .edit-btn-touch-hitarea')
      .attr('pointer-events', (d: DisplayNode) => {
        const visible = showHovered ? this.isNodeHovered(d.id) : this.isNodeSelected(d.id);
        return visible ? 'all' : 'none';
      });
  }

  // ===========================================================================
  // PER-NODE BUTTON RENDERING
  // ===========================================================================

  /**
   * Render edit buttons for a single node inside its SVG group.
   *
   * Layout uses polar coordinates (radius = AXIS_DIST = 58):
   *   Top:    −parent at -15°  (−15, −56)    ＋parent at +15° (or 0, −56)   × at +45° (+41, −41)
   *   Bottom: −child  at -15°  (−15, +56)    ＋child  at +15° (or 0, +56)   caret at -45° (+41, +41)
   *
   * When a − button exists, the ＋ button sits at +15° (symmetric around vertical).
   * When no − exists, the ＋ sits at 0° (centered on the vertical axis).
   * The fold/unfold caret is NOT duplicated here.
   */
  private renderButtonsForNode(
    nodeEl: Selection<SVGGElement, DisplayNode, BaseType | null, unknown>,
    node: DisplayNode,
  ): void {
    // Check if this is a folded main root
    const isFoldedMainRoot = node.isMainRoot && !this.isNodeExpanded(node.id);

    // Always rebuild: remove old buttons and recreate fresh.
    // This ensures −/＋ buttons properly appear/disappear after link changes.
    nodeEl.select('.edit-button-group').remove();

    const pool = this.nodePool();
    const parentCount = this.countParents(node, pool);
    const childCount = node.data.children?.length ?? 0;

    const shouldShow = this.shouldShowButtons(node);

    const btnGroup = nodeEl.append('g')
      .attr('class', 'edit-button-group')
      .style('opacity', shouldShow ? 1 : 0)
      .style('pointer-events', shouldShow ? 'auto' : 'none');

    if (isFoldedMainRoot) {
      // Folded main root: only show delete button (×)
      // The unfold button (caret) is rendered separately by GraphVisualizer
      this.createButton(btnGroup, {
        cx: DIAG_OFF,
        cy: -DIAG_OFF,
        label: '×',
        onClick: () => {
          if (this._pendingDeleteNodeId === node.id) {
            this._pendingDeleteNodeId = null;
            this.onDeleteNode(node);
          } else {
            this.disarmDelete();
            this._pendingDeleteNodeId = node.id;
            this.styleDeleteButton(node.id, true);
          }
        },
      });
    } else {
      // Unfolded recipe or non-root node: show all buttons

      // ── Top row ──

      // Delete parent link (−) at -15° — only if node has parents
      if (parentCount > 0) {
        this.createButton(btnGroup, {
          cx: -POLAR_SIN,
          cy: -POLAR_COS,
          label: '−',
          onClick: () => {
            this.disarmDelete();
            this.onDeleteParentLink(node);
          },
        });
      }

      // Add parent link (+) — same y as − when paired, centered when alone
      this.createButton(btnGroup, {
        cx: parentCount > 0 ? POLAR_SIN : 0,
        cy: parentCount > 0 ? -POLAR_COS : -AXIS_DIST,
        label: '+',
        onClick: () => {
          this.disarmDelete();
          this.onAddParentLink(node);
        },
      });

      // Delete node (×) at +45° — two-click confirmation
      this.createButton(btnGroup, {
        cx: DIAG_OFF,
        cy: -DIAG_OFF,
        label: '×',
        onClick: () => {
          if (this._pendingDeleteNodeId === node.id) {
            // Second click — actually delete
            this._pendingDeleteNodeId = null;
            this.onDeleteNode(node);
          } else {
            // First click — arm (disarm any previously armed first)
            this.disarmDelete();
            this._pendingDeleteNodeId = node.id;
            this.styleDeleteButton(node.id, true);
          }
        },
      });

      // ── Bottom row ──

      // Delete child link (−) at -15° — only if node has children
      if (childCount > 0) {
        this.createButton(btnGroup, {
          cx: -POLAR_SIN,
          cy: POLAR_COS,
          label: '−',
          onClick: () => {
            this.disarmDelete();
            this.onDeleteChildLink(node);
          },
        });
      }

      // Add child link (+) — same y as − when paired, centered when alone
      this.createButton(btnGroup, {
        cx: childCount > 0 ? POLAR_SIN : 0,
        cy: childCount > 0 ? POLAR_COS : AXIS_DIST,
        label: '+',
        onClick: () => {
          this.disarmDelete();
          this.onAddChildLink(node);
        },
      });

      // Note: fold/unfold caret is at bottom-right (DIAG_OFF, DIAG_OFF) at -45°.
    }

    // Mobile touch hit area — with pointer-events matching visibility.
    // The hit area has explicit pointer-events:all, which overrides the
    // parent group's pointer-events:none, so we must set it here too.
    if (this.isMobile) {
      this.addMobileTouchHitArea(btnGroup);
      if (!shouldShow) {
        btnGroup.selectAll('.edit-btn-touch-hitarea')
          .attr('pointer-events', 'none');
      }
    }
  }

  // ===========================================================================
  // BUTTON CREATION
  // ===========================================================================

  /**
   * Create a single edit button matching the caret style:
   *   - black circle (#0d1117)
   *   - green accent border (var(--accent-alpha-40))
   *   - green symbol (var(--accent))
   *   - border width 1.5 (same as caret)
   */
  private createButton(
    group: Selection<SVGGElement, DisplayNode, BaseType | null, unknown>,
    params: { cx: number; cy: number; label: string; onClick: () => void },
  ): void {
    const btn = group.append('g').attr('class', 'edit-btn').style('cursor', 'pointer');

    // Circle — same stroke/fill as the caret
    btn.append('circle')
      .attr('r', BTN_RADIUS)
      .attr('cx', params.cx)
      .attr('cy', params.cy)
      .attr('fill', BTN_FILL)
      .attr('stroke', BTN_STROKE)
      .attr('stroke-width', BTN_STROKE_WIDTH);

    // Symbol — green accent
    btn.append('text')
      .attr('x', params.cx)
      .attr('y', params.cy)
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'middle')
      .attr('dy', 1)
      .attr('fill', BTN_SYMBOL_FILL)
      .attr('font-size', BTN_ICON_SIZE)
      .attr('font-weight', 'bold')
      .attr('pointer-events', 'none')
      .text(params.label);

    // Use mousedown (not click) so the link-flow mousemove/mouseup handlers
    // are set up before the user starts dragging (needed for drag-to-create).
    // Also prevents D3 drag behavior on the parent .viz-node from activating.
    btn.on('mousedown', (event: MouseEvent) => {
      event.stopPropagation();
      params.onClick();
    });

    // Prevent click synthesis from reaching the parent .viz-node, which has a
    // click handler that calls disarmDelete() — this would immediately disarm
    // the × button's pending delete on desktop (on mobile, preventDefault()
    // on touchstart blocks click synthesis entirely, so it works there).
    btn.on('click', (event: MouseEvent) => {
      event.stopPropagation();
    });

    // On mobile: call startAddLinkFlow immediately on touchstart so that
    // the SVG-level capturing-phase handlers (setupLinkFlowMouseMove) can
    // track the touch for drag-to-create. stopPropagation() prevents D3
    // drag on the parent .viz-node. preventDefault() prevents browser
    // scroll (though SVG's touch-action: none already covers this).
    if (this.isMobile) {
      btn.on('touchstart', (event: TouchEvent) => {
        event.preventDefault();
        event.stopPropagation();

        const touch = event.touches[0];
        this.touchStartPos = { x: touch.clientX, y: touch.clientY };
        this.touchStartTime = Date.now();

        // Start the link flow immediately so SVG-level handlers can track the drag
        params.onClick(); // Calls startAddLinkFlow() or handleDeleteLink()
      });
      // touchmove and touchend are handled by SVG capturing-phase handlers
      // added in setupLinkFlowMouseMove / setupLinkFlowListeners / startDeleteLinkFlow
    }
  }

  // ===========================================================================
  // MOBILE TOUCH HIT AREA
  // ===========================================================================

  /**
   * Add invisible 44×44 rect behind each button for touch targets.
   */
  private addMobileTouchHitArea(
    group: Selection<SVGGElement, DisplayNode, BaseType | null, unknown>,
  ): void {
    group.selectAll('.edit-btn').each(function (this: BaseType) {
      const btnEl = d3.select<BaseType, unknown>(this);
      const circle = btnEl.select('circle');
      if (circle.empty()) return;

      const cx = parseFloat(circle.attr('cx') || '0');
      const cy = parseFloat(circle.attr('cy') || '0');

      btnEl.insert('rect', 'circle')
        .attr('class', 'edit-btn-touch-hitarea')
        .attr('x', cx - 22)
        .attr('y', cy - 22)
        .attr('width', 44)
        .attr('height', 44)
        .attr('fill', 'transparent')
        .attr('pointer-events', 'all')
        .attr('touch-action', 'none');
    });
  }

  // ===========================================================================
  // VISIBILITY HELPERS
  // ===========================================================================

  private shouldShowButtons(node: DisplayNode): boolean {
    return !!(this.isNodeSelected(node.id) || this.isNodeHovered(node.id));
  }

  private applyVisibility(
    nodeEl: Selection<SVGGElement, DisplayNode, BaseType | null, unknown>,
    node: DisplayNode,
  ): void {
    const show = this.shouldShowButtons(node);
    nodeEl.select('.edit-button-group')
      .style('opacity', show ? 1 : 0)
      .style('pointer-events', show ? 'auto' : 'none');
  }

  // ===========================================================================
  // CLEAN UP
  // ===========================================================================

  private removeButtonsForNonRecipeNodes(recipeId: string): void {
    this.nodesGroup.selectAll<SVGGElement, DisplayNode>('.viz-node').each(function (d: DisplayNode) {
      if (d.recipeId !== recipeId) {
        d3.select<SVGGElement, DisplayNode>(this).select('.edit-button-group').remove();
      }
    });
  }

  // ===========================================================================
  // PARENT COUNT
  // ===========================================================================

  private countParents(node: DisplayNode, pool: Map<string, DisplayNode>): number {
    let count = 0;
    for (const other of pool.values()) {
      if (other.id === node.id) continue;
      if (other.recipeId !== node.recipeId) continue;
      if (other.data.children?.includes(node.id)) count++;
    }
    return count;
  }
}
